/**
 * @fileoverview Framework-agnostic auth-intent handshake.
 * @module features/analytics/authIntent
 *
 * @description
 * Auth providers that redirect (OAuth, magic links, SSO) tear the "user clicked
 * log in" moment away from the "user id is now known" moment — they happen on
 * different page loads, so you can't track the login inline. This tiny module is
 * the bridge: stash the intent (which flow, which method, which page) in
 * sessionStorage BEFORE the redirect, then read it back AFTER, from any
 * framework. No React, no globals — plain functions the SDK builds `markLogin` /
 * `markSignup` / `completeAuth` on top of.
 */

import { getWindow, isBrowser } from "../../utils/browser";

export type AuthMode = "login" | "signup";

export interface AuthIntentInput {
  /** Auth method — "google", "github", "email", "sso", … */
  method?: string;
  /** Signup traffic source (e.g. "pricing"). Signup only. */
  source?: string;
  /** Page the auth started on. Auto-detected in the browser when omitted. */
  path?: string;
  /** Full URL the auth started on. Auto-detected when omitted. */
  url?: string;
}

export interface AuthIntent extends AuthIntentInput {
  mode: AuthMode;
  /** When the intent was stashed (ms epoch) — used to expire stale intents. */
  ts: number;
}

const KEY = "fg_auth_intent";
// An intent older than this is treated as abandoned (user bailed on the flow).
const MAX_AGE_MS = 30 * 60 * 1000;

function storage(): Storage | null {
  if (!isBrowser()) return null;
  try {
    return getWindow()?.sessionStorage ?? null;
  } catch {
    // Access can throw in sandboxed iframes / privacy modes.
    return null;
  }
}

/** Stash an auth intent before a redirect. No-op outside the browser. */
export function stashAuthIntent(mode: AuthMode, input: AuthIntentInput = {}): void {
  const store = storage();
  if (!store) return;
  const win = getWindow();
  const intent: AuthIntent = {
    mode,
    method: input.method,
    source: input.source,
    path: input.path ?? win?.location?.pathname,
    url: input.url ?? win?.location?.href,
    ts: Date.now(),
  };
  try {
    store.setItem(KEY, JSON.stringify(intent));
  } catch {
    /* quota / private mode — nothing we can do, drop it */
  }
}

/** Read the pending auth intent, or null if none / expired / unreadable. */
export function readAuthIntent(): AuthIntent | null {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(KEY);
    if (!raw) return null;
    const intent = JSON.parse(raw) as AuthIntent;
    if (!intent || (intent.mode !== "login" && intent.mode !== "signup")) return null;
    if (typeof intent.ts === "number" && Date.now() - intent.ts > MAX_AGE_MS) {
      clearAuthIntent();
      return null;
    }
    return intent;
  } catch {
    return null;
  }
}

/** Clear the pending auth intent. No-op outside the browser. */
export function clearAuthIntent(): void {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(KEY);
  } catch {
    /* noop */
  }
}
